import { Button, IconButton } from "@dropbox/dig-components/buttons";
import { Checkbox } from "@dropbox/dig-components/controls";
import { Menu } from "@dropbox/dig-components/menu";
import { UIIcon } from "@dropbox/dig-icons";
import { FilterLine } from "@dropbox/dig-icons/assets";
import useConnectors from "@mirage/service-connectors/useConnectors";
import { type ConnectorName } from "@mirage/shared/connectors";
import { ConnectorIcon } from "@mirage/shared/icons";
import i18n from "@mirage/translations";
import { useMediaQuery } from "@react-hookz/web";
import { memo, useCallback, useMemo } from "react";

import type { MenuPlacement } from "@dropbox/dig-components/menu";

interface SourcesFilterProps {
  onFilterChange: (appTypes: Set<string>) => void;
  selectedAppTypes: Set<string>;
  placement?: MenuPlacement;
  mobilePlacement?: MenuPlacement;
  onMenuOpen?: (isOpen: boolean) => void;
}

export const SourcesFilter = memo(
  ({
    onFilterChange,
    selectedAppTypes,
    placement = "bottom-end",
    mobilePlacement = "bottom",
    onMenuOpen,
  }: SourcesFilterProps) => {
    const isMobile = useMediaQuery("(max-width: 600px)");
    const effectivePlacement = isMobile ? mobilePlacement : placement;
    const { connectors } = useConnectors();

    const appTypes = useMemo(() => {
      const types = new Map<string, string>();

      // Add only connected connectors
      connectors.forEach((connector) => {
        if (!connector.id_attrs?.type || !connector.branding?.display_name)
          return;
        types.set(connector.id_attrs.type, connector.branding.display_name);
      });

      // Sort alphabetically by display name
      return Array.from(types.entries()).sort((a, b) =>
        a[1].toLowerCase().localeCompare(b[1].toLowerCase()),
      );
    }, [connectors]);

    const handleFilterSelect = useCallback(
      (appType: string, event: React.MouseEvent) => {
        event.stopPropagation();
        const newSelectedAppTypes = new Set(selectedAppTypes);
        if (newSelectedAppTypes.has(appType)) {
          newSelectedAppTypes.delete(appType);
        } else {
          newSelectedAppTypes.add(appType);
        }
        onFilterChange(newSelectedAppTypes);
      },
      [onFilterChange, selectedAppTypes],
    );

    const handleDeselectAll = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        onFilterChange(new Set());
      },
      [onFilterChange],
    );

    return (
      <Menu.Wrapper
        closeOnSelection={false}
        onToggle={(event) => onMenuOpen?.(event.isOpen)}
        shouldPropagateClickOutsideMouseEvents={true}
      >
        {({ getContentProps, getTriggerProps, closeMenu }) => (
          <>
            <IconButton
              {...getTriggerProps()}
              variant="borderless"
              aria-label={i18n.t("filter_by_app")}
            >
              <UIIcon src={FilterLine} />
            </IconButton>

            <Menu.Content {...getContentProps()} placement={effectivePlacement}>
              <Menu.Segment
                withLabel={
                  <>
                    {i18n.t("filter_by")}
                    {selectedAppTypes.size > 0 && (
                      <Button
                        size="small"
                        variant="transparent"
                        onClick={(event: React.MouseEvent) => {
                          event.stopPropagation();
                          closeMenu({});
                          handleDeselectAll(event);
                        }}
                      >
                        {i18n.t("deselect_all")}
                      </Button>
                    )}
                  </>
                }
              >
                {appTypes.map(([appType, displayName]) => (
                  <Menu.ActionItem
                    key={appType}
                    value={appType}
                    role="menuitemcheckbox"
                    aria-checked={selectedAppTypes.has(appType)}
                    onClick={(event) => handleFilterSelect(appType, event)}
                    className="unlocalizableString"
                    withLeftAccessory={
                      <Checkbox
                        checked={selectedAppTypes.has(appType)}
                        readOnly
                        tabIndex={-1}
                      />
                    }
                    withRightAccessory={
                      <ConnectorIcon
                        connectorName={appType as ConnectorName}
                        iconUrl={
                          connectors.find((c) => c.id_attrs?.type === appType)
                            ?.branding?.icon_src
                        }
                        size={20}
                      />
                    }
                  >
                    {displayName}
                  </Menu.ActionItem>
                ))}
              </Menu.Segment>
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>
    );
  },
);

SourcesFilter.displayName = "SourcesFilter";
